import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import { swellNext, swellWarn, wakeFork, wakeLeft, wakeRowing, type BattleState } from '@somnhold/sim';
import { BOTE_LANE, boteForkZ, simToWorld } from '@somnhold/render';
import { pinMark, remeasureMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';

/** A wolf's head swimming, its wake behind. */
const WOLF = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8.5 6l1.2-3 1.4 2.4 2.4.6-1 2.2.4 2.3H8.2L6.8 8.3z" fill="currentColor"/><path d="M1 11.5c1.4-.7 2.8-.7 4.2 0s2.8.7 4.2 0 2.8-.7 4.2 0M1 14c1.4-.7 2.8-.7 4.2 0s2.8.7 4.2 0" stroke="currentColor" stroke-width="1.1" fill="none" stroke-linecap="round"/></svg>';
/** A fork: one way parting in three. */
const FORK = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 15V8M8 8L3 2.5M8 8V2M8 8l5-5.5" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round"/></svg>';
/** An oar. */
const OAR = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 13.5l8-8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><path d="M10 6l1.6-4 2.4 2.4-4 1.6z" fill="currentColor"/></svg>';
/** The boat. */
const BOAT = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 8.5h13l-2.3 3.6H3.8z" fill="currentColor"/><path d="M12.8 8.5l1.6-3M4 8.5V6.2M7 8.5V6.2M10 8.5V6.2" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/><path d="M1 14c1.2-.8 2.4-.8 3.6 0s2.4.8 3.6 0 2.4-.8 3.6 0 2.4.8 3.6 0" stroke="currentColor" stroke-width="1" fill="none"/></svg>';
/** A big wave breaking. */
const WAVE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1 12.5c2-.2 3.2-1.6 3.6-4 .5-3 2.6-5 5.4-5 2 0 3.6 1.1 4.2 2.8-1.6-.6-3.4 0-3.8 1.8-.4 1.6.8 3 2.4 3.2-1.3 1-3 1.4-4.6 1.2" stroke="currentColor" stroke-width="1.4" fill="none" stroke-linecap="round"/><path d="M1 15c1.4-.6 2.8-.6 4.2 0s2.8.6 4.2 0 2.8-.6 4.2 0" stroke="currentColor" stroke-width="1" fill="none"/></svg>';
/** Someone in the water, an arm up. */
const SWIM = '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="6.5" r="2.2" fill="currentColor"/><path d="M10 6l2-4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M1 11c1.4-.7 2.8-.7 4.2 0s2.8.7 4.2 0 2.8-.7 4.2 0" stroke="currentColor" stroke-width="1.2" fill="none" stroke-linecap="round"/></svg>';
/** A floe of ice. */
const FLOE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 10.5l2-4 4-1.5 3 1 3 3.5-1.5 2H3.5z" fill="currentColor"/><path d="M1 14c1.4-.6 2.8-.6 4.2 0s2.8.6 4.2 0 2.8-.6 4.2 0" stroke="currentColor" stroke-width="1" fill="none"/></svg>';
/** A shield, round, with its boss. */
const SHIELD = '<svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6.2" fill="currentColor"/><circle cx="8" cy="8" r="1.8" fill="#14161b"/></svg>';
/** A wolf's head raised, howling. */
const HOWL = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 13.5l1.8-5.2L3.6 4l2.6 1.8L9.4 2l.8 3.4 3.3 1.4-2.7 1.4L9 13.5z" fill="currentColor"/><path d="M12.8 2.6c.9.7 1.4 1.6 1.4 2.6M11.9 1.2c1.5.9 2.4 2.4 2.5 4" stroke="currentColor" stroke-width="1" fill="none" stroke-linecap="round"/></svg>';
/** Deep water: rings going down. */
const DEEP = '<svg viewBox="0 0 16 16" aria-hidden="true"><ellipse cx="8" cy="9" rx="6.5" ry="3.6" fill="currentColor" opacity=".45"/><ellipse cx="8" cy="9" rx="3.6" ry="1.9" fill="currentColor"/></svg>';
/** The current: two arrows, in (closing) or out (opening); a bar (still). */
const IN = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M1 8h5M3.6 5.4L6.2 8 3.6 10.6M15 8h-5M12.4 5.4L9.8 8l2.6 2.6" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const OUT = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6 8H1M3.4 5.4L.8 8l2.6 2.6M10 8h5M12.6 5.4L15.2 8l-2.6 2.6" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const STILL = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 6.5h12M2 9.5h12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

type Mark = { el: HTMLButtonElement; state: HTMLElement; point: THREE.Vector3; at: number; tag?: HTMLElement; chips?: HTMLElement };

/**
 * Bera's boat on screen (docs/sendas/barbaro.md, chapter 3), inside a path's own plate (game/senda-hud.ts) and over the
 * board; a phone's plate kept under about a quarter of the screen:
 *
 * - the wake (`wake`): the distance to the Wolf (a bar, Ivar's tick on it), the fork in sight (its number, the seconds
 *   till Bera turns), the oars (a pip each: rowing, broken) and the spares, the boat's blows, the rule in a line. Over
 *   the fork, a mark on each channel's mouth: its side, the current (in, out, still), the pack's lines in it; the
 *   widest carries Ivar's tag; the one tapped, «por acá»; the Wolf's ash where it is (on, or a smear at the mouth).
 * - the swell (`swell`): the big waves (a pip each passed; the next one's seconds; the bow rising: «¡a un escudo!»), who
 *   is in the water and for how long, the lost, the boat. Over the board, the floes (a tap swings the hero over; the
 *   wolves waiting on each), the boat while on a floe, the nearest shields while the bow rises, a mark on each one in
 *   the water.
 * - the old howler (`elder`): its life and what it's doing (fighting, looking at the water, swimming, under, cut off),
 *   the leap's warning, the boat, the rule. Over the board, the floes' tips: the dark water off two of them, the one it
 *   goes for in red (held at the screen's edge when it's off it: Bera's arrow).
 *
 * Plain DOM, written only when it changes (docs/RENDIMIENTO.md); the board's points from the sim's own rules.
 */
export class BoteHud {
  readonly panel: HTMLElement;
  private kind: 'wake' | 'swell' | 'elder';
  private v = new THREE.Vector3();
  private rows: HTMLElement[] = [];
  private marks: Mark[] = [];
  private key = '';
  // Shared: the boat.
  private boatRow = h('div.bh-row.bt-boatrow');
  private boatFill = h('i');
  private boatText = h('small.vt-boat-s');
  // The wake.
  private wolfRow = h('div.bh-row.bt-wolf');
  private wolfFill = h('i');
  private ivarTick = h('b.bt-ivar');
  private wolfText = h('small.vt-boat-s');
  private forkRow = h('div.bh-row.al-waverow.bt-fork');
  private forkText = h('span.al-wave');
  private oarRow = h('div.bh-row.bt-oars');
  private oarPips: HTMLElement[] = [];
  private oarText = h('small.vt-boat-s');
  // The swell.
  private waveRow = h('div.bh-row.al-waverow.bt-wave');
  private wavePips: HTMLElement[] = [];
  private waveText = h('span.al-wave');
  private sweptRow = h('div.bh-row.al-waverow.bt-swept');
  private sweptText = h('span.al-wave');
  private shields: HTMLElement[] = [];
  private heads: HTMLElement[] = [];
  // The old howler.
  private elderRow = h('div.bh-row.bt-elder');
  private elderFill = h('i');
  private elderText = h('small.vt-boat-s');
  private leapRow = h('div.bh-row.al-waverow.bt-leap');
  private leapText = h('span.al-wave');

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private names: Record<string, string>,
    private mark: (at: number) => void,
  ) {
    const br = s.rules?.senda?.bote;
    this.kind = br?.wake ? 'wake' : br?.swell ? 'swell' : 'elder';
    this.boatRow.append(h('span.bh-ico', { html: BOAT }), h('span.bh-label', {}, t('senda.bote.boat')), h('span.bar.vt-bar', {}, this.boatFill), this.boatText);
    if (br?.wake) {
      this.wolfRow.append(h('span.bh-ico', { html: WOLF }), h('span.bh-label', {}, t('senda.bote.wolf')), h('span.bar.vt-bar.bt-dist', {}, this.wolfFill, this.ivarTick), this.wolfText);
      this.ivarTick.title = t('senda.bote.ivar');
      this.forkRow.append(h('span.bh-ico', { html: FORK }), this.forkText);
      this.oarPips = br.wake.boat.oars.map(() => h('i'));
      this.oarRow.append(h('span.bh-ico', { html: OAR }), h('span.bh-label', {}, t('senda.bote.oars')), h('span.al-pips.vt-pips.bt-oarpips', {}, ...this.oarPips), this.oarText);
      this.rows.push(this.wolfRow, this.forkRow, this.oarRow, this.boatRow, h('div.bh-row.vt-rule', {}, h('span.bh-ico', { html: WOLF }), h('span.vt-rule-t', {}, t('senda.bote.rule'))));
      for (let k = 0; k < 3; k++) this.marks.push(this.channel(k));
    } else if (br?.swell) {
      const r = br.swell;
      this.wavePips = Array.from({ length: r.need }, () => h('i'));
      this.waveRow.append(h('span.bh-ico', { html: WAVE }), h('span.bh-label', {}, t('senda.bote.waves')), h('span.al-pips.vt-pips', {}, ...this.wavePips), this.waveText);
      this.sweptRow.append(h('span.bh-ico', { html: SWIM }), h('span.bh-label', {}, t('senda.bote.lost')), this.sweptText);
      this.rows.push(this.waveRow, this.sweptRow, this.boatRow);
      const boat = this.make(0, BOAT, t('senda.bote.toBoat'), t('senda.bote.toBoatHint'), 'boat');
      this.marks.push(boat);
      r.floes.forEach((p, k) => {
        const m = this.make(k + 1, FLOE, t('senda.bote.floe'), t('senda.bote.floeHint'), `floe-${k}`);
        simToWorld(p.x, p.y, m.point, 0.6);
        this.marks.push(m);
      });
      for (let i = 0; i < 2; i++) {
        const c = h('div.vt-post.bt-shield', {}, h('span.vt-post-i', { html: SHIELD }), t('senda.bote.shield'));
        c.hidden = true;
        this.board.append(c);
        this.shields.push(c);
      }
      for (let i = 0; i < 3; i++) {
        const c = h('div.vt-post.bt-head', {}, h('span.vt-post-i', { html: SWIM }), h('span.bt-head-t'));
        c.hidden = true;
        this.board.append(c);
        this.heads.push(c);
      }
    } else if (br?.elder) {
      const r = br.elder;
      this.elderRow.append(h('span.bh-ico', { html: HOWL }), h('span.bh-label', {}, t('senda.bote.elder')), h('span.bar.vt-bar.bt-elderbar', {}, this.elderFill), this.elderText);
      this.leapRow.append(h('span.bh-ico', { html: HOWL }), this.leapText);
      this.rows.push(this.elderRow, this.leapRow, this.boatRow, h('div.bh-row.vt-rule', {}, h('span.bh-ico', { html: DEEP }), h('span.vt-rule-t', {}, t('senda.bote.rule3'))));
      r.tips.forEach((p, k) => {
        const m = this.make(k, DEEP, t('senda.bote.dark'), t('senda.bote.darkHint'), `tip-${k}`);
        m.el.classList.add('bt-tip');
        simToWorld(p.x, p.y, m.point, 0.4);
        this.marks.push(m);
      });
    }
    this.panel = h(`div.bh.cz-panel.vt-panel.bt-panel.bt-${this.kind}`, {}, ...this.rows);
  }

  private make(at: number, icon: string, name: string, hint: string, test: string): Mark {
    const state = h('small.al-state.cz-state');
    const el = h(
      `button.al-stone.cz-mark.vt-mark.bt-mark.bt-${test.split('-')[0]}`,
      { type: 'button', 'data-test': `bote-${test}`, 'aria-label': hint, title: hint, onpointerup: (e: Event) => (e.stopPropagation(), this.mark(at)) },
      h('span.al-ring'),
      h('span.al-glyph', { html: icon }),
      name ? h('b.mn-name.cz-name', {}, name) : '',
      state,
    ) as HTMLButtonElement;
    this.board.append(el);
    return { el, state, point: new THREE.Vector3(), at };
  }

  /** A channel's mark: its side, and under it the current, the ash and the pack's lines. */
  private channel(k: number): Mark {
    const m = this.make(k, FORK, '', t('senda.bote.chHint'), `ch-${k}`);
    m.el.classList.add('bt-ch');
    const tag = h('b.bt-ivartag', {}, t('senda.bote.ivarWay'));
    const chips = h('span.bt-chips');
    m.el.append(tag, chips);
    m.tag = tag;
    m.chips = chips;
    return m;
  }

  private setText(el: HTMLElement, s: string): void {
    if (el.textContent !== s) el.textContent = s;
  }

  private boat(s: BattleState): void {
    const b = s.senda?.npcs.find((n) => n.id === 'boat');
    if (!b) return;
    const k = Math.max(0, Math.min(1, b.hp / Math.max(1, b.maxHp)));
    setStyle(this.boatFill, 'width', `${(k * 100).toFixed(1)}%`);
    this.setText(this.boatText, t('senda.hud.hits', { n: b.hp }));
    this.boatRow.classList.toggle('warn', b.hp * 3 <= b.maxHp);
  }

  private updateWake(s: BattleState): void {
    const r = s.rules?.senda?.bote?.wake;
    const w = s.senda?.bote?.wake;
    if (!r || !w) return;
    setStyle(this.wolfFill, 'width', `${(w.dist / 100).toFixed(1)}%`);
    setStyle(this.ivarTick, 'left', `${Math.min(98, w.ivar / 100).toFixed(1)}%`);
    this.setText(this.wolfText, w.passed ? t('senda.bote.passed') : t('senda.bote.behind'));
    this.wolfRow.classList.toggle('warn', w.dist < 2000);
    this.wolfRow.classList.toggle('go', w.passed);
    const f = wakeFork(s);
    const stalled = w.stage === 1 && w.how[w.how.length - 1] === 3;
    const left = wakeLeft(s);
    this.setText(
      this.forkText,
      w.done >= 0 ? t('senda.bote.passed') : stalled ? t('senda.bote.stall') : f ? `${t('senda.bote.fork', { n: w.fork + 1, of: r.forks.length })} · ${t('senda.bote.forkIn', { s: Math.max(0, Math.ceil(left / TICK_HZ)) })}` : t('senda.bote.forkNext', { s: Math.max(0, Math.ceil((w.at - s.waveT) / TICK_HZ)) }),
    );
    this.forkRow.classList.toggle('warn', stalled || (!!f && left < 5 * TICK_HZ));
    w.oars.forEach((o, i) => {
      const p = this.oarPips[i];
      if (!p) return;
      p.classList.toggle('on', o.mode === 0);
      p.classList.toggle('bad', o.mode === 1 || o.mode === 3);
    });
    this.setText(this.oarText, t('senda.bote.oarsN', { n: wakeRowing(w), k: w.spares }));
    this.oarRow.classList.toggle('warn', w.oars.some((o) => o.mode !== 0));
    const key = `${w.fork}|${w.stage}|${w.pick}|${!!f}|${s.result}`;
    if (key === this.key) return;
    this.key = key;
    this.marks.forEach((m, k) => {
      const c = f?.channels[k];
      const gone = !f || !c || s.result !== 'running';
      m.el.classList.toggle('gone', gone);
      m.el.disabled = gone;
      if (gone || !c || !f || !m.chips || !m.tag) return;
      m.el.classList.toggle('picked', w.pick === k);
      m.tag.hidden = f.wide !== k;
      // What is seen in the water there, drawn as it looks and never said (docs/DECISIONS.md, 2026-11-04, «la estela»):
      // the current's chevrons (in, out, none), the grey of his ash (a long strip on through, or a smudge at the mouth),
      // the pack's thin white lines. Which way to go is the player's to read off them, crossed.
      m.chips.replaceChildren(
        h(`span.bt-chip.bt-glyph.bt-drift${c.drift < 0 ? '.in' : c.drift > 0 ? '.out' : ''}`, { 'aria-label': t(c.drift < 0 ? 'senda.bote.closing' : c.drift > 0 ? 'senda.bote.opening' : 'senda.bote.steady') }, h('span.bt-chip-i', { html: c.drift < 0 ? IN : c.drift > 0 ? OUT : STILL })),
        c.ash || c.smear ? h(`span.bt-chip.bt-glyph.bt-ashbar${c.ash ? '' : '.smear'}`, { 'aria-label': t(c.ash ? 'senda.bote.ash' : 'senda.bote.smear') }, h('i')) : '',
        c.pack ? h('span.bt-chip.bt-glyph.bt-lines', { 'aria-label': c.pack === 1 ? t('senda.bote.pack1') : t('senda.bote.pack', { n: c.pack }) }, ...Array.from({ length: c.pack }, () => h('i'))) : '',
      );
      const st = w.pick === k ? t('senda.bote.picked') : t(`senda.bote.ch.${c.lane}`);
      if (m.state.textContent !== st) m.state.textContent = st;
      remeasureMark(m.el);
    });
  }

  private updateSwell(s: BattleState): void {
    const r = s.rules?.senda?.bote?.swell;
    const w = s.senda?.bote?.swell;
    if (!r || !w) return;
    this.wavePips.forEach((p, i) => p.classList.toggle('on', i < w.wave));
    const warn = swellWarn(s) && w.done < 0;
    const left = swellNext(s);
    this.setText(this.waveText, w.done >= 0 ? t('senda.bote.goalWaves', { n: w.wave, of: r.need }) : warn ? t('senda.bote.waveNow', { s: Math.max(1, Math.ceil(left / TICK_HZ)) }) : t('senda.bote.waveIn', { s: Math.ceil(Math.max(0, left) / TICK_HZ) }));
    this.waveRow.classList.toggle('warn', warn);
    const sw = w.swept.find((p) => !p.lost && p.until >= 0);
    this.setText(this.sweptText, sw ? t('senda.bote.swept', { name: this.names[sw.id] ?? sw.id, s: Math.max(0, Math.ceil((sw.until - s.tick) / TICK_HZ)) }) : t('senda.bote.lostN', { n: w.lost, of: r.lostMax }));
    this.sweptRow.classList.toggle('warn', !!sw || w.lost > 0);
    const aboard = w.on < 0 && w.to === -1;
    for (const m of this.marks) {
      let gone = w.done >= 0 || s.result !== 'running' || w.to !== -1;
      if (m.at === 0) {
        gone = gone || w.on < 0;
        const p = r.floes[w.on];
        if (p) simToWorld(p.x > 0 ? 900 : -900, p.y, m.point, 0.7);
      } else {
        gone = gone || !aboard;
        const n = w.waiting.filter((q) => q[1] === m.at - 1).length;
        const st = n ? `${n} · ${t('senda.bote.onFloe')}` : '';
        if (m.state.textContent !== st) {
          m.state.textContent = st;
          remeasureMark(m.el);
        }
        m.el.classList.toggle('hot', n > 0);
      }
      m.el.classList.toggle('gone', gone);
      m.el.disabled = gone;
    }
  }

  private updateElder(s: BattleState): void {
    const r = s.rules?.senda?.bote?.elder;
    const w = s.senda?.bote?.elder;
    if (!r || !w) return;
    const e = s.enemies.find((x) => x.id === w.id);
    const k = e ? Math.max(0, e.hp) / Math.max(1, e.maxHp) : 0;
    setStyle(this.elderFill, 'width', `${(k * 100).toFixed(1)}%`);
    const st =
      w.phase === 1 ? t('senda.bote.elderLook') : w.phase === 2 ? t('senda.bote.elderSwim') : w.phase === 3 ? t('senda.bote.elderUnder', { s: Math.max(0, Math.ceil((w.until - s.tick) / TICK_HZ)) }) : w.phase === 4 ? t('senda.bote.elderCut') : t('senda.bote.elderFight');
    this.setText(this.elderText, st);
    this.elderRow.classList.toggle('warn', w.phase === 1 || w.phase === 2 || w.phase === 3);
    this.elderRow.classList.toggle('go', w.phase === 4);
    const leap = w.leapT >= 0;
    this.setText(this.leapText, leap ? t('senda.bote.elderLeap') : w.phase === 0 ? t('senda.bote.leapIn', { s: Math.max(0, Math.ceil((w.leapNext - s.waveT) / TICK_HZ)) }) : '');
    this.leapRow.classList.toggle('warn', leap);
    const key = `${w.dark}|${w.phase}|${w.tip}|${s.result}`;
    if (key === this.key) return;
    this.key = key;
    for (const m of this.marks) {
      const dark = !!((w.dark >> m.at) & 1);
      const target = (w.phase === 1 || w.phase === 2) && w.tip === m.at;
      const gone = (!dark && !target) || w.done >= 0 || s.result !== 'running';
      m.el.classList.toggle('gone', gone);
      m.el.disabled = true;
      m.el.classList.toggle('target', target);
      const txt = target ? t('senda.bote.elderLook') : '';
      if (m.state.textContent !== txt) {
        m.state.textContent = txt;
        remeasureMark(m.el);
      }
    }
  }

  update(s: BattleState): void {
    this.boat(s);
    if (this.kind === 'wake') this.updateWake(s);
    else if (this.kind === 'swell') this.updateSwell(s);
    else this.updateElder(s);
  }

  /** Every frame: the marks over their places (the fork's on its channels as it comes); the shields, the swept. */
  place(s: BattleState | undefined, camera: THREE.Camera, w: number, ht: number): void {
    const wake = s?.rules?.senda?.bote?.wake;
    const fz = s && wake ? boteForkZ(s) : null;
    for (const m of this.marks) {
      if (m.el.classList.contains('gone')) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      if (wake && fz !== null && s) {
        const f = wakeFork(s);
        const c = f?.channels[m.at];
        if (c) m.point.set(c.lane * BOTE_LANE, 0.4, fz + 4.5);
      }
      this.v.copy(m.point).project(camera);
      const target = m.el.classList.contains('target');
      if (this.v.z > 1 || (!target && (this.v.x < -1.1 || this.v.x > 1.1 || this.v.y < -1.1 || this.v.y > 1.1))) {
        setStyle(m.el, 'display', 'none');
        continue;
      }
      setStyle(m.el, 'display', '');
      // The tip it goes for stays on screen, at the edge if it's off it (Bera's arrow).
      const edge = target && (Math.abs(this.v.x) > 0.92 || Math.abs(this.v.y) > 0.92);
      m.el.classList.toggle('edge', edge);
      const x = (Math.max(-0.92, Math.min(0.92, this.v.x)) * 0.5 + 0.5) * w;
      const y = (-Math.max(-0.92, Math.min(0.92, this.v.y)) * 0.5 + 0.5) * ht;
      pinMark(m.el, Math.min(w - 40, Math.max(40, x)), y);
    }
    const r = s?.rules?.senda?.bote?.swell;
    const sw = s?.senda?.bote?.swell;
    const hero = s?.heroes[0];
    // The swept: a mark where each went in, its seconds.
    if (r && sw && s) {
      const out = sw.swept.filter((p) => !p.lost && p.until >= 0);
      this.heads.forEach((c, i) => {
        const p = out[i];
        if (!p) {
          c.hidden = true;
          return;
        }
        simToWorld(p.x + (p.x >= 0 ? 1600 : -1600), p.y + 600, this.v, 0.8).project(camera);
        c.hidden = this.v.z > 1;
        const tx = c.querySelector('.bt-head-t');
        const txt = `${this.names[p.id] ?? p.id} · ${Math.max(0, Math.ceil((p.until - s.tick) / TICK_HZ))} s`;
        if (tx && tx.textContent !== txt) tx.textContent = txt;
        pinMark(c, Math.min(w - 40, Math.max(40, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht - 16);
      });
    }
    const warn = !!r && !!sw && !!s && sw.done < 0 && sw.on < 0 && swellWarn(s);
    if (!warn || !r || !hero) {
      for (const c of this.shields) c.hidden = true;
      return;
    }
    const near = r.boat.shields
      .map((p, i) => ({ i, d: (p.x - hero.x) ** 2 + (p.y - hero.y) ** 2 }))
      .sort((a, b) => a.d - b.d)
      .slice(0, this.shields.length);
    near.forEach((n, k) => {
      const c = this.shields[k] as HTMLElement;
      const p = r.boat.shields[n.i] as { x: number; y: number };
      simToWorld(p.x, p.y, this.v, 1.0).project(camera);
      c.hidden = this.v.z > 1;
      pinMark(c, Math.min(w - 30, Math.max(30, (this.v.x * 0.5 + 0.5) * w)), (-this.v.y * 0.5 + 0.5) * ht - 18);
    });
  }
}
